import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";

export type ParsedPhoneNumber = {
  isValid: boolean;
  formattedNumber: string;
};

/**
 * Parses and formats a phone number for display and interaction.
 *
 * This function attempts to parse a phone number using the provided country code as a hint,
 * and falls back to parsing without a country code if the first attempt fails.
 *
 * Unlike strict validation, this returns success if the number can be parsed and formatted,
 * even if it doesn't pass libphonenumber-js's strict isValid() check. This ensures
 * edge-case numbers (like some with unusual area codes) can still be displayed and used
 * for call/SMS actions.
 *
 * @param phoneNumber - The phone number string to parse (e.g., "+15551234567")
 * @param countryCode - Optional ISO country code to use as parsing hint (e.g., "US")
 * @returns Object with isValid flag and formattedNumber (in international format)
 *
 * @example
 * ```typescript
 * const result = parsePhoneNumber("+15711507235", "US");
 * if (result.isValid) {
 *   console.log(result.formattedNumber); // "+1 571 150 7235"
 * }
 * ```
 */
export const parsePhoneNumber = (phoneNumber?: string, countryCode?: CountryCode): ParsedPhoneNumber => {
  if (!phoneNumber) {
    return { isValid: false, formattedNumber: "" };
  }

  // Try parsing with country code hint first, then without
  let parsedPhone = countryCode ? parsePhoneNumberFromString(phoneNumber, countryCode) : null;
  if (!parsedPhone) {
    parsedPhone = parsePhoneNumberFromString(phoneNumber);
  }

  // If we can parse and format the number, consider it valid for our purposes
  if (parsedPhone) {
    const formattedNumber = parsedPhone.formatInternational();
    return { isValid: true, formattedNumber };
  }

  return { isValid: false, formattedNumber: "" };
};
